import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { HourglassEmpty } from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { getCurrentDate } from '../../utils/dateUtils';

const hourglassFlip = keyframes`
  0%, 45% { transform: rotate(0deg); }
  50%, 95% { transform: rotate(180deg); }
  100% { transform: rotate(360deg); }
`;

const parseTimeString = (timeStr, baseDate = null) => {
  if (!timeStr) return null;

  // Check if it's ISO date format (for shop items)
  if (timeStr.includes('-') || timeStr.includes('T')) {
    return new Date(timeStr);
  }

  // HH:MM format (for tasks) - create Date for today at that time
  const parts = timeStr.split(':');
  if (parts.length < 2) return null;

  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);

  if (isNaN(h) || isNaN(m)) return null;

  const today = baseDate || getCurrentDate();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate(), h, m, 0, 0);
};

const formatRemaining = (ms, isDateBased = false, compact = false) => {
  if (ms <= 0) return null;

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (compact) {
    if (days > 0) return `⏳${days} pv`;
    if (hours >= 2) return `⏳${hours}h`;
    if (hours >= 1) return `⏳${hours}h ${minutes % 60}min`;
    return `⏳${minutes}min`;
  }

  if (isDateBased && days > 0) {
    return days === 1 ? '1 päivä' : `${days} päivää`;
  }

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }

  if (minutes > 0) {
    return `${minutes}m`;
  }

  return `${seconds}s`;
};

const LiveTimer = ({
  startTime,
  endTime,
  variant = 'task',
  size = 'medium',
  compact = false,
  onExpired,
}) => {
  const [now, setNow] = useState(() => getCurrentDate().getTime());

  const isDateBased = useMemo(() => {
    return endTime && (endTime.includes('-') || endTime.includes('T'));
  }, [endTime]);

  const { status, remainingMs, startDate } = useMemo(() => {
    const currentTime = new Date(now);
    const start = parseTimeString(startTime, currentTime);
    const end = parseTimeString(endTime, currentTime);

    if (!end) {
      return { status: 'invalid', remainingMs: 0, startDate: null };
    }

    // For date-based (shop), compare full timestamps
    // For time-based (tasks), compare within today
    if (start && currentTime < start) {
      return {
        status: 'waiting',
        remainingMs: start.getTime() - currentTime.getTime(),
        startDate: start,
      };
    }

    if (currentTime > end) {
      return {
        status: 'expired',
        remainingMs: 0,
        startDate: start,
      };
    }

    return {
      status: 'active',
      remainingMs: end.getTime() - currentTime.getTime(),
      startDate: start,
    };
  }, [now, startTime, endTime]);

  // Determine update interval based on remaining time
  const updateInterval = useMemo(() => {
    if (status === 'expired') return null;
    if (remainingMs <= 5 * 60 * 1000) return 1000; // 1s when < 5 min
    if (remainingMs <= 60 * 60 * 1000) return 10000; // 10s when < 1 hour
    return 30000; // 30s otherwise
  }, [status, remainingMs]);

  useEffect(() => {
    if (!updateInterval) return;

    const interval = setInterval(() => {
      setNow(getCurrentDate().getTime());
    }, updateInterval);

    return () => clearInterval(interval);
  }, [updateInterval]);

  // Call onExpired when status changes to expired
  useEffect(() => {
    if (status === 'expired' && onExpired) {
      onExpired();
    }
  }, [status, onExpired]);

  const isUrgent = status === 'active' && remainingMs <= 5 * 60 * 1000;
  const isVeryUrgent = status === 'active' && remainingMs <= 60 * 1000;

  const colors = useMemo(() => {
    switch (status) {
      case 'waiting':
        return { icon: '#9e9e9e', text: '#757575', bg: '#f5f5f5' };
      case 'active':
        if (isVeryUrgent) return { icon: '#d32f2f', text: '#c62828', bg: '#ffebee' };
        if (isUrgent) return { icon: '#ff9800', text: '#e65100', bg: '#fff3e0' };
        return { icon: '#4caf50', text: '#2e7d32', bg: '#e8f5e9' };
      case 'expired':
        return { icon: '#9e9e9e', text: '#757575', bg: '#fafafa' };
      default:
        return { icon: '#9e9e9e', text: '#757575', bg: '#f5f5f5' };
    }
  }, [status, isUrgent, isVeryUrgent]);

  const displayText = useMemo(() => {
    if (status === 'waiting' && startDate) {
      const timeStr = startTime?.includes(':')
        ? startTime
        : startDate.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
      return compact ? `⏳${timeStr}` : `Alkaa klo ${timeStr}`;
    }

    if (status === 'active') {
      const remaining = formatRemaining(remainingMs, isDateBased, compact);
      if (compact) return remaining;
      return remaining ? `Aikaa jäljellä ${remaining}` : null;
    }

    if (status === 'expired') {
      return compact ? null : 'Aika umpeutui';
    }

    return null;
  }, [status, startDate, startTime, remainingMs, isDateBased, compact]);

  const shouldAnimate = status === 'active';
  const animationDuration = isVeryUrgent ? '1s' : isUrgent ? '1.5s' : '3s';

  const iconSize = size === 'small' ? 16 : 20;
  const fontSize = size === 'small' ? '0.75rem' : '0.875rem';

  if (!displayText) return null;

  // Compact mode: inline with spinning hourglass, inherits parent color/size
  if (compact) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <HourglassEmpty
          sx={{
            fontSize: 'inherit',
            color: 'inherit',
            animation: shouldAnimate ? `${hourglassFlip} ${animationDuration} ease-in-out infinite` : 'none',
          }}
        />
        <span>{displayText.replace('⏳', '')}</span>
      </Box>
    );
  }

  return (
    <Box
      role="timer"
      aria-live="polite"
      aria-label={`Aikaa jäljellä: ${displayText}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: size === 'small' ? 0.75 : 1,
        py: size === 'small' ? 0.25 : 0.5,
        borderRadius: 1,
        bgcolor: colors.bg,
      }}
    >
      <HourglassEmpty
        sx={{
          fontSize: iconSize,
          color: colors.icon,
          animation: shouldAnimate ? `${hourglassFlip} ${animationDuration} ease-in-out infinite` : 'none',
        }}
      />
      <Typography
        variant="caption"
        sx={{
          fontSize,
          fontWeight: 600,
          color: colors.text,
          lineHeight: 1,
        }}
      >
        {displayText}
      </Typography>
    </Box>
  );
};

export default LiveTimer;
